import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Package, Truck, AlertTriangle, CheckCircle2, Search, RefreshCw,
  ArrowRight, Box
} from 'lucide-react';

const InventoryATPTab = () => {
  const [searchSku, setSearchSku] = useState('');

  const atpItems = [
    { sku: 'SKU-8821', name: 'Premium Olive Oil 5L', physical: 1200, reserved: 400, atp: 800, status: 'ok', substitution: null },
    { sku: 'SKU-8822', name: 'Olive Oil Blend 5L', physical: 500, reserved: 100, atp: 400, status: 'ok', substitution: null },
    { sku: 'SKU-7701', name: 'Tomato Paste 1kg', physical: 200, reserved: 250, atp: 0, status: 'out', substitution: 'SKU-7702' },
    { sku: 'SKU-7702', name: 'Tomato Paste 1kg (Alt)', physical: 800, reserved: 100, atp: 700, status: 'ok', substitution: null },
    { sku: 'SKU-5500', name: 'Rice Basmati 25kg', physical: 3500, reserved: 1200, atp: 2300, status: 'ok', substitution: null },
  ];

  const inboundPOs = [
    { po: 'PO-2024-089', sku: 'SKU-7701', qty: 2000, eta: '2024-03-18', supplier: 'Agri Foods' },
    { po: 'PO-2024-091', sku: 'SKU-8821', qty: 500, eta: '2024-03-22', supplier: 'Oil Co' },
  ];

  const filtered = atpItems.filter(
    (i) => i.sku.toLowerCase().includes(searchSku.toLowerCase()) || i.name.toLowerCase().includes(searchSku.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Inventory & Available-to-Promise (ATP)</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">ATP stock</h3>
        <p className="text-sm text-gray-600 mb-4">
          Available to sell = Physical stock − Stock reserved for other pending orders. Prevents double-selling.
        </p>
        <div className="relative max-w-xs mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by SKU or name..."
            value={searchSku}
            onChange={(e) => setSearchSku(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Physical</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reserved</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ATP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Substitution</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((row) => (
                <tr key={row.sku} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.sku}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.physical.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.reserved.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className={`font-medium ${row.atp === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {row.atp.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {row.substitution ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Suggest: {row.substitution}
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Inbound stock (open POs)</h3>
          <p className="text-sm text-gray-500 mt-1">When an item is out of stock, see when the next shipment arrives for backorders.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ETA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {inboundPOs.map((po) => (
                <tr key={po.po} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{po.po}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{po.sku}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{po.qty.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{po.eta}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{po.supplier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-teal-900">Live order tracking</p>
            <p className="text-sm text-teal-800">Reps and clients can track status: Processing → Packed → Dispatched → Delivered without calling the warehouse.</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">Smart substitutions</p>
            <p className="text-sm text-amber-800">When an item is out of stock, the system suggests the closest alternative to save the sale.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryATPTab;
